import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { listingsAPI, metaAPI, uploadAPI, authAPI, formatApiError } from '../lib/api';
import { ImagePlus, X, Loader2, Upload, Crown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';

export default function CreateListing() {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [brands, setBrands] = useState([]);
  const [countries, setCountries] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [uploading, setUploading] = useState(false);
  const [premium, setPremium] = useState({ is_premium: false, max_images: 3, max_description: 50 });
  const [form, setForm] = useState({
    title: '', description: '', vehicle_type: 'cars_suvs', brand: '', model: '',
    year: 2024, price: '', currency: 'EUR', mileage: '', fuel_type: '',
    transmission: '', power_hp: '', color: '', country: '', city: '', images: []
  });

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    Promise.all([
      metaAPI.getBrands().then(r => setBrands(r.data.brands)),
      metaAPI.getCountries().then(r => setCountries(r.data.countries)),
      authAPI.premiumStatus().then(r => setPremium(r.data)).catch(() => {}),
    ]).catch(() => {});
  }, [user, navigate]);

  const updateForm = (key, value) => setForm(prev => ({ ...prev, [key]: value }));
  const addImage = () => {
    if (form.images.length >= premium.max_images) {
      toast.error(`Max ${premium.max_images} photos. ${!premium.is_premium ? 'Upgrade to premium for 10!' : ''}`);
      return;
    }
    if (imageUrl.trim()) { updateForm('images', [...form.images, imageUrl.trim()]); setImageUrl(''); }
  };
  const removeImage = (idx) => updateForm('images', form.images.filter((_, i) => i !== idx));

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    setUploading(true);
    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const newImages = [...form.images];
    for (const file of files) {
      if (newImages.length >= premium.max_images) {
        toast.error(`Max ${premium.max_images} photos. ${!premium.is_premium ? 'Upgrade to premium for 10!' : ''}`);
        break;
      }
      try {
        const { data } = await uploadAPI.image(file);
        newImages.push(`${backendUrl}${data.url}`);
      } catch (err) {
        toast.error(`Failed to upload ${file.name}`);
      }
    }
    updateForm('images', newImages);
    setUploading(false);
    e.target.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Please fill in the title'); return; }
    if (!isCarTransport && (!form.model || !form.price || !form.country || !form.city)) {
      toast.error('Please fill in all required fields'); return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, year: parseInt(form.year), price: parseFloat(form.price), mileage: form.mileage ? parseInt(form.mileage) : null, power_hp: form.power_hp ? parseInt(form.power_hp) : null, brand: form.brand || null };
      const { data } = await listingsAPI.create(payload);
      toast.success('Listing created!');
      navigate(`/listings/${data.id}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSubmitting(false); }
  };

  const vehicleTypes = ['cars_suvs', 'trucks_buses', 'caravans_campers', 'motorcycles_bikes', 'industrial', 'car_transport', 'yachts_boats'];
  const fuelTypes = ['petrol', 'diesel', 'electric', 'hybrid', 'lpg', 'cng'];
  const inputCls = "bg-[#0B0F19] border-[#1E293B] text-[#F1F5F9] placeholder:text-[#475569]";
  const isCarTransport = form.vehicle_type === 'car_transport';

  return (
    <div className="min-h-screen bg-[#0B0F19]" data-testid="create-listing-page">
      <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-8">
        <h1 className="text-2xl sm:text-3xl font-semibold text-[#F1F5F9] mb-6 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
          {t('create.title')}
        </h1>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="vehicle-info-section">
            <h2 className="text-base font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('create.vehicleInfo')}</h2>
            <div className="space-y-4">
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Title *</label>
                <Input value={form.title} onChange={e => updateForm('title', e.target.value)} required className={inputCls} data-testid="input-title" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Category *</label>
                  <Select value={form.vehicle_type} onValueChange={v => updateForm('vehicle_type', v)}>
                    <SelectTrigger data-testid="select-vehicle-type"><SelectValue /></SelectTrigger>
                    <SelectContent>{vehicleTypes.map(vt => <SelectItem key={vt} value={vt}>{t(`categories.${vt}`)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {!isCarTransport && (
                  <div>
                    <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.brand')}</label>
                    <Input value={form.brand} onChange={e => updateForm('brand', e.target.value)} className={inputCls} data-testid="input-brand" />
                  </div>
                )}
              </div>
              {!isCarTransport && (
                <>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Model *</label>
                      <Input value={form.model} onChange={e => updateForm('model', e.target.value)} required={!isCarTransport} className={inputCls} data-testid="input-model" />
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('listing.year')} *</label>
                      <Input type="number" value={form.year} onChange={e => updateForm('year', e.target.value)} min={1900} max={2030} className={inputCls} data-testid="input-year" />
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('listing.mileage')}</label>
                      <Input type="number" value={form.mileage} onChange={e => updateForm('mileage', e.target.value)} className={inputCls} data-testid="input-mileage" />
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('listing.fuel')}</label>
                      <Select value={form.fuel_type} onValueChange={v => updateForm('fuel_type', v)}>
                        <SelectTrigger data-testid="select-fuel"><SelectValue placeholder="-" /></SelectTrigger>
                        <SelectContent>{fuelTypes.map(f => <SelectItem key={f} value={f}>{t(`fuel_types.${f}`)}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('listing.transmission')}</label>
                      <Select value={form.transmission} onValueChange={v => updateForm('transmission', v)}>
                        <SelectTrigger data-testid="select-transmission"><SelectValue placeholder="-" /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="manual">{t('transmission_types.manual')}</SelectItem>
                          <SelectItem value="automatic">{t('transmission_types.automatic')}</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('listing.power')} (HP)</label>
                      <Input type="number" value={form.power_hp} onChange={e => updateForm('power_hp', e.target.value)} className={inputCls} data-testid="input-power" />
                    </div>
                    <div>
                      <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('listing.color')}</label>
                      <Input value={form.color} onChange={e => updateForm('color', e.target.value)} className={inputCls} data-testid="input-color" />
                    </div>
                  </div>
                </>
              )}
              <div>
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('listing.description')} *</label>
                <Textarea value={form.description} onChange={e => {
                  if (e.target.value.length <= premium.max_description) updateForm('description', e.target.value);
                }} rows={4} required maxLength={premium.max_description} className={`${inputCls}`} data-testid="input-description" />
                <div className="flex items-center justify-between mt-1">
                  <span className={`text-[10px] ${form.description.length >= premium.max_description ? 'text-red-400' : 'text-[#475569]'}`}>
                    {form.description.length}/{premium.max_description}
                  </span>
                  {!premium.is_premium && (
                    <span className="text-[10px] text-[#FADB5F] flex items-center gap-1">
                      <Crown className="w-3 h-3" /> Premium: up to 200 characters
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {!isCarTransport && (
            <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="pricing-section">
              <h2 className="text-base font-semibold text-[#F1F5F9] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('create.pricing')}</h2>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">Price (EUR) *</label>
                  <Input type="number" value={form.price} onChange={e => updateForm('price', e.target.value)} required className={inputCls} data-testid="input-price" />
                </div>
                <div>
                  <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">{t('filters.country')} *</label>
                  <Select value={form.country} onValueChange={v => updateForm('country', v)}>
                    <SelectTrigger data-testid="select-country"><SelectValue placeholder={t('filters.allCountries')} /></SelectTrigger>
                    <SelectContent>{countries.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="mt-4">
                <label className="text-xs tracking-[0.15em] uppercase font-bold text-[#94A3B8] mb-1.5 block">City *</label>
                <Input value={form.city} onChange={e => updateForm('city', e.target.value)} required={!isCarTransport} className={inputCls} data-testid="input-city" />
              </div>
            </div>
          )}

          <div className="bg-[#141B2D] rounded-xl border border-[#1E293B] p-6" data-testid="photos-section">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-base font-semibold text-[#F1F5F9]" style={{ fontFamily: 'Outfit, sans-serif' }}>{t('create.photos')}</h2>
              <span className={`text-xs ${form.images.length >= premium.max_images ? 'text-red-400' : 'text-[#94A3B8]'}`}>
                {form.images.length}/{premium.max_images}
                {!premium.is_premium && <span className="text-[#FADB5F] ml-2"><Crown className="w-3 h-3 inline" /> Premium: 10</span>}
              </span>
            </div>
            
            {/* File Upload */}
            {form.images.length < premium.max_images ? (
              <label className="flex flex-col items-center justify-center w-full h-28 rounded-xl border-2 border-dashed border-[#1E293B] hover:border-[#FADB5F]/50 bg-[#0B0F19] cursor-pointer transition-colors mb-4" data-testid="file-upload-area">
                <input type="file" accept="image/jpeg,image/png,image/webp,image/gif" multiple onChange={handleFileUpload} className="hidden" data-testid="file-upload-input" />
                {uploading ? (
                  <Loader2 className="w-6 h-6 animate-spin text-[#FADB5F] mb-1" />
                ) : (
                  <Upload className="w-6 h-6 text-[#475569] mb-1" />
                )}
                <span className="text-xs text-[#94A3B8]">{uploading ? 'Uploading...' : 'Click to upload photos'}</span>
                <span className="text-[10px] text-[#475569] mt-0.5">JPEG, PNG, WebP, GIF (max 10MB)</span>
              </label>
            ) : (
              <div className="flex items-center justify-center w-full h-20 rounded-xl border-2 border-dashed border-red-900/30 bg-red-950/10 mb-4">
                <span className="text-xs text-red-400">Max {premium.max_images} photos reached {!premium.is_premium && '— Upgrade to premium for 10!'}</span>
              </div>
            )}

            {/* URL input as alternative */}
            <div className="flex gap-2 mb-4">
              <Input value={imageUrl} onChange={e => setImageUrl(e.target.value)} placeholder="https://" className={`flex-1 ${inputCls}`} data-testid="input-image-url"
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addImage(); } }} />
              <Button type="button" onClick={addImage} variant="outline" className="border-[#1E293B] text-[#94A3B8]" data-testid="add-image-btn">
                <ImagePlus className="w-4 h-4" />
              </Button>
            </div>
            {form.images.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                {form.images.map((img, i) => (
                  <div key={i} className="relative aspect-video rounded-lg overflow-hidden border border-[#1E293B] group">
                    <img src={img} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImage(i)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`remove-image-${i}`}>
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <Button type="submit" disabled={submitting} className="w-full h-12 bg-[#FADB5F] text-[#0F172A] hover:bg-[#E5C34A] font-semibold text-base" data-testid="submit-listing-btn">
            {submitting ? <Loader2 className="w-5 h-5 animate-spin" /> : t('create.submit')}
          </Button>
        </form>
      </div>
    </div>
  );
}
